<template>
  <aside
    class="td"
    :class="{ 'td--narrow': narrow, 'td--done': isDone }"
    data-test="task-drawer"
    @keydown.esc="onEscape"
  >
    <div v-if="pop" class="td-scrim" data-test="task-drawer-scrim" @click="closePop"></div>

    <!-- Header: complete / reopen · id + copy link · delete · close -->
    <header class="td-head" data-test="task-drawer-header">
      <button
        type="button"
        class="td-complete"
        :class="{ 'td-complete--done': isDone }"
        data-test="task-drawer-complete"
        :disabled="busy || !canEdit"
        @click="toggleComplete"
      >
        <td-icon :name="isDone ? 'undo' : 'check'" :size="16" />
        {{ isDone ? 'Reopen' : 'Mark complete' }}
      </button>
      <span class="td-head-spacer"></span>
      <span class="td-id" :title="doc.id" data-test="task-drawer-id">{{ idLabel }}</span>
      <button
        type="button"
        class="td-icon-btn"
        :title="copied ? 'Link copied' : 'Copy link'"
        :aria-label="copied ? 'Link copied' : 'Copy link to this task'"
        data-test="task-drawer-copy-link"
        @click="copyLink"
      >
        <td-icon :name="copied ? 'check' : 'link'" :size="18" />
      </button>
      <button
        v-if="rights.all || rights.thisOne"
        type="button"
        class="td-icon-btn"
        title="Delete task"
        aria-label="Delete task"
        data-test="task-drawer-delete"
        :disabled="busy"
        @click="confirmingDelete = !confirmingDelete"
      >
        <td-icon name="trash" :size="18" />
      </button>
      <button type="button" class="td-icon-btn" title="Close" aria-label="Close" data-test="task-drawer-close" @click="close">
        <td-icon name="x" :size="18" />
      </button>
    </header>

    <div v-if="confirmingDelete" class="td-confirm" role="alert" data-test="task-drawer-delete-confirm">
      <span class="td-confirm-text">{{ rights.repeating ? 'Delete this repeating task?' : 'Delete this task?' }}</span>
      <div class="td-confirm-actions">
        <button
          v-if="rights.thisOne"
          type="button"
          class="td-btn td-btn--secondary"
          data-test="task-drawer-delete-this"
          :disabled="busy"
          @click="deleteThisOne"
        >Only this one</button>
        <button
          v-if="rights.all"
          type="button"
          class="td-btn td-btn--danger"
          data-test="task-drawer-delete-all"
          :disabled="busy"
          @click="deleteAll"
        >{{ rights.repeating ? 'All of them' : 'Delete' }}</button>
        <button type="button" class="td-btn td-btn--ghost" data-test="task-drawer-delete-cancel" @click="confirmingDelete = false">Cancel</button>
      </div>
    </div>

    <div class="td-body">
      <div v-if="loadError" class="td-notice" role="alert" data-test="task-drawer-load-error">{{ loadError }}</div>

      <!-- Title (the same @ / # editor TodoDetails uses) -->
      <section class="td-title-wrap" data-test="task-drawer-title-section">
        <at at="#" :members="patientList" name-key="PatientWithDOB" ref="patientat">
          <template v-slot:embeddedItem="s">
            <span>
              <span :data-teamio-patientid="s.current.PatNum" class="badge mr-xs badge-secondary badge-pill mt-1">
                <router-link :to="{ name: 'patientprofile', params: { patientID: officeSiteID + '-' + s.current.PatNum }}">{{ s.current.Patient }}</router-link>
              </span>
            </span>
          </template>
          <at at="@" :members="activeEmployees" name-key="Name" :value="contentValue" ref="empat" @input="onTitleInput">
            <template slot="item" slot-scope="s">
              <span>{{ s.item.Name }}</span>
            </template>
            <template slot="embeddedItem" slot-scope="s">
              <span>
                <span :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{ s.current.Name }}</span>
              </span>
            </template>
            <div
              ref="editor"
              class="td-title"
              :class="{ 'td-title--done': isDone }"
              contenteditable
              placeholder="Task title"
              data-test="task-drawer-title"
              @blur="flushTitle"
            ></div>
          </at>
        </at>
        <p class="td-created" data-test="task-drawer-created">
          <span v-if="createdText">{{ createdText }}</span>
          <span v-if="completedText" class="td-created-done">{{ completedText }}</span>
        </p>
      </section>

      <!-- Properties -->
      <section class="td-grid" data-test="task-drawer-properties">
        <!-- Assigned to -->
        <div class="td-field" data-test="task-drawer-assignees">
          <span class="td-label">Assigned to</span>
          <div class="td-chips">
            <span v-for="p in assigneeChips" :key="p.id" class="td-chip td-chip--person" :data-test="`task-drawer-assignee-${p.id}`">
              <span class="td-avatar">{{ p.initials }}</span>{{ p.label }}
            </span>
            <span v-if="!assigneeChips.length && !canEditPeople" class="td-empty">Nobody</span>
            <button
              v-if="canEditPeople"
              type="button"
              class="td-add"
              data-test="task-drawer-assign"
              @click="openPop('assign')"
            >
              <td-icon name="plus" :size="14" />{{ assigneeChips.length ? 'Change' : 'Assign' }}
            </button>
          </div>
          <div v-if="pop === 'assign'" class="td-pop" data-test="task-drawer-assign-popover">
            <input
              ref="assignSearch"
              v-model="assignQuery"
              type="text"
              class="td-input"
              placeholder="Find a teammate"
              data-test="task-drawer-assign-search"
            />
            <ul class="td-menu">
              <li v-for="e in assignChoices" :key="e.EmployeeID">
                <button
                  type="button"
                  class="td-menu-item"
                  :class="{ 'td-menu-item--on': isAssigned(e.EmployeeID) }"
                  :data-test="`task-drawer-assign-option-${e.EmployeeID}`"
                  @click="toggleAssignee(e.EmployeeID)"
                >
                  <span class="td-avatar">{{ initials(e.Name) }}</span>
                  <span class="td-menu-text">{{ e.EmployeeID === currentUserId ? `${e.Name} (you)` : e.Name }}</span>
                  <td-icon v-if="isAssigned(e.EmployeeID)" name="check" :size="16" class="td-menu-check" />
                </button>
              </li>
              <li v-if="!assignChoices.length" class="td-menu-empty">No one matches “{{ assignQuery }}”</li>
            </ul>
          </div>
        </div>

        <!-- Patient -->
        <div class="td-field" data-test="task-drawer-patients">
          <span class="td-label">Patient</span>
          <div class="td-chips">
            <span v-for="p in patientChips" :key="p.id" class="td-chip td-chip--person" :data-test="`task-drawer-patient-${p.id}`">
              <span class="td-avatar td-avatar--patient">{{ p.initials }}</span>{{ p.name }}
              <button
                v-if="canEdit"
                type="button"
                class="td-chip-x"
                :aria-label="`Remove ${p.name}`"
                :data-test="`task-drawer-patient-remove-${p.id}`"
                @click="removePatient(p.id)"
              ><td-icon name="x" :size="12" /></button>
            </span>
            <button
              v-if="canEdit"
              type="button"
              class="td-add"
              data-test="task-drawer-add-patient"
              @click="openPop('patient')"
            >
              <td-icon name="plus" :size="14" />Add patient
            </button>
          </div>
          <div v-if="pop === 'patient'" class="td-pop" :class="{ 'td-pop--end': !narrow }" data-test="task-drawer-patient-popover">
            <input
              ref="patientSearch"
              v-model="patientQuery"
              type="text"
              class="td-input"
              placeholder="Search patients"
              data-test="task-drawer-patient-search"
            />
            <ul class="td-menu">
              <li v-for="p in patientChoices" :key="p.PatNum">
                <button
                  type="button"
                  class="td-menu-item"
                  :data-test="`task-drawer-patient-option-${p.PatNum}`"
                  @click="addPatient(p)"
                >
                  <span class="td-menu-text">{{ p.PatientWithDOB || p.Patient }}</span>
                </button>
              </li>
              <li v-if="patientQuery.trim().length < 2" class="td-menu-empty">Type at least 2 letters</li>
              <li v-else-if="!patientChoices.length" class="td-menu-empty">No patient matches “{{ patientQuery }}”</li>
            </ul>
          </div>
        </div>

        <!-- Due date -->
        <div class="td-field" data-test="task-drawer-due">
          <span class="td-label">Due date</span>
          <div class="td-chips">
            <button
              type="button"
              class="td-pick"
              :class="{ 'td-pick--late': isLate, 'td-pick--muted': !dueDay }"
              :disabled="!canEdit"
              data-test="task-drawer-due-button"
              @click="openPop('due')"
            >
              <td-icon name="cal" :size="15" />{{ dueText }}
            </button>
          </div>
          <div v-if="pop === 'due'" class="td-pop" data-test="task-drawer-due-popover">
            <div class="td-presets">
              <button
                v-for="p in presets"
                :key="p.key"
                type="button"
                class="td-preset"
                :class="{ 'td-preset--on': p.ymd === dueDay }"
                :data-test="`task-drawer-due-preset-${p.key}`"
                @click="setDue(p.ymd)"
              >{{ p.label }}</button>
            </div>
            <label class="td-date-label">
              <span>Pick a date</span>
              <input
                type="date"
                class="td-input td-date"
                :value="dueDay || ''"
                data-test="task-drawer-due-input"
                @change="setDue($event.target.value)"
              />
            </label>
            <p v-if="skipNote" class="td-hint">{{ skipNote }}</p>
          </div>
        </div>

        <!-- Repeat -->
        <div class="td-field" data-test="task-drawer-repeat">
          <span class="td-label">Repeat</span>
          <div class="td-chips">
            <button
              type="button"
              class="td-pick"
              :class="{ 'td-pick--muted': !isRepeating }"
              :disabled="!canEdit"
              data-test="task-drawer-repeat-button"
              @click="openPop('repeat')"
            >
              <td-icon name="repeat" :size="15" />{{ repeatText }}
            </button>
          </div>
          <div v-if="pop === 'repeat'" class="td-pop" :class="{ 'td-pop--end': !narrow }" data-test="task-drawer-repeat-popover">
            <ul class="td-menu">
              <li v-if="repeatKey === null">
                <span class="td-menu-item td-menu-item--on td-menu-item--static">
                  <span class="td-menu-text">{{ repeatText }}</span>
                  <td-icon name="check" :size="16" class="td-menu-check" />
                </span>
              </li>
              <li v-for="o in repeatChoices" :key="o.key">
                <button
                  type="button"
                  class="td-menu-item"
                  :class="{ 'td-menu-item--on': o.key === repeatKey }"
                  :data-test="`task-drawer-repeat-option-${o.key}`"
                  @click="setRepeat(o.key)"
                >
                  <span class="td-menu-text">{{ o.label }}</span>
                  <td-icon v-if="o.key === repeatKey" name="check" :size="16" class="td-menu-check" />
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- Tags -->
        <div class="td-field td-field--wide" data-test="task-drawer-tags">
          <span class="td-label">Tags</span>
          <div class="td-chips">
            <span v-for="g in doc.tags || []" :key="g" class="td-chip" :data-test="`task-drawer-tag-${g}`">
              <td-icon name="tag" :size="13" />{{ g }}
              <button
                v-if="canEdit"
                type="button"
                class="td-chip-x"
                :aria-label="`Remove tag ${g}`"
                :data-test="`task-drawer-tag-remove-${g}`"
                @click="removeTag(g)"
              ><td-icon name="x" :size="12" /></button>
            </span>
            <button v-if="canEdit" type="button" class="td-add" data-test="task-drawer-add-tag" @click="openPop('tag')">
              <td-icon name="plus" :size="14" />Add tag
            </button>
          </div>
          <div v-if="pop === 'tag'" class="td-pop" data-test="task-drawer-tag-popover">
            <input
              ref="tagSearch"
              v-model="tagQuery"
              type="text"
              class="td-input"
              placeholder="Find or create a tag"
              data-test="task-drawer-tag-search"
              @keydown.enter.prevent="onTagEnter"
            />
            <ul class="td-menu">
              <li v-if="tagOptions.create">
                <button type="button" class="td-menu-item td-menu-item--create" data-test="task-drawer-tag-create" @click="createTag(tagOptions.create)">
                  <td-icon name="plus" :size="14" />
                  <span class="td-menu-text">Create tag <strong>“{{ tagOptions.create }}”</strong></span>
                </button>
              </li>
              <li v-for="g in tagOptions.suggestions" :key="g">
                <button type="button" class="td-menu-item" :data-test="`task-drawer-tag-option-${g}`" @click="addTag(g)">
                  <td-icon name="tag" :size="13" />
                  <span class="td-menu-text">{{ g }}</span>
                </button>
              </li>
              <li v-if="tagOptions.already" class="td-menu-empty">“{{ tagQuery.trim() }}” is already on this task.</li>
              <li v-else-if="!tagOptions.create && !tagOptions.suggestions.length" class="td-menu-empty">No more tags</li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Subtasks -->
      <section class="td-section" data-test="task-drawer-subtasks">
        <h3 class="td-h">
          Subtasks
          <span v-if="subProgress.total" class="td-count" data-test="task-drawer-subtasks-count">{{ subProgress.done }}/{{ subProgress.total }}</span>
        </h3>
        <draggable
          v-if="subtasks.length"
          :value="subtasks"
          tag="ul"
          class="td-subs"
          handle=".td-sub-grip"
          :disabled="!canEdit"
          @end="onSubtaskDragEnd"
        >
          <li
            v-for="(s, i) in subtasks"
            :key="s.id || i"
            class="td-sub"
            :class="{ 'td-sub--done': s.status === 'COMPLETED' }"
            :data-test="`task-drawer-subtask-${i}`"
          >
            <span v-if="canEdit" class="td-sub-grip" title="Drag to reorder"><td-icon name="grip" :size="14" /></span>
            <button
              type="button"
              class="td-check"
              :class="{ 'td-check--on': s.status === 'COMPLETED' }"
              :aria-label="s.status === 'COMPLETED' ? 'Mark subtask not done' : 'Mark subtask done'"
              :disabled="!canEdit"
              :data-test="`task-drawer-subtask-toggle-${i}`"
              @click="toggleSubtask(i)"
            ><td-icon v-if="s.status === 'COMPLETED'" name="check" :size="12" /></button>
            <span class="td-sub-text">{{ s.content }}</span>
            <button
              v-if="canEdit"
              type="button"
              class="td-icon-btn td-icon-btn--sm"
              aria-label="Remove subtask"
              :data-test="`task-drawer-subtask-remove-${i}`"
              @click="removeSubtask(i)"
            ><td-icon name="x" :size="14" /></button>
          </li>
        </draggable>
        <label v-if="canEdit" class="td-sub-add">
          <td-icon name="plus" :size="16" />
          <input
            v-model="subtaskDraft"
            type="text"
            class="td-sub-input"
            placeholder="Add a subtask and press Enter"
            data-test="task-drawer-subtask-input"
            @keydown.enter.prevent="addSubtask"
          />
        </label>
      </section>

      <!-- Attachments (wiki pages) -->
      <section class="td-section" data-test="task-drawer-attachments">
        <h3 class="td-h">
          Attachments
          <span v-if="attachments.length" class="td-count">{{ attachments.length }}</span>
          <span class="td-head-spacer"></span>
          <span class="td-attach-wrap">
            <button v-if="canEdit" type="button" class="td-link-btn" data-test="task-drawer-attach" @click="openPop('attach')">
              <td-icon name="plus" :size="14" />Attach
            </button>
            <div v-if="pop === 'attach'" class="td-pop td-pop--end" data-test="task-drawer-attach-popover">
              <input
                ref="attachSearch"
                v-model="attachQuery"
                type="text"
                class="td-input"
                placeholder="Find a wiki page"
                data-test="task-drawer-attach-search"
              />
              <ul class="td-menu">
                <li v-for="w in attachChoices" :key="w.WikiPageID">
                  <button
                    type="button"
                    class="td-menu-item"
                    :class="{ 'td-menu-item--on': isAttached(w.WikiPageID) }"
                    :data-test="`task-drawer-attach-option-${w.WikiPageID}`"
                    @click="toggleAttachment(w.WikiPageID)"
                  >
                    <td-icon name="book" :size="14" />
                    <span class="td-menu-text">{{ w.Name }}</span>
                    <td-icon v-if="isAttached(w.WikiPageID)" name="check" :size="16" class="td-menu-check" />
                  </button>
                </li>
                <li v-if="!(wikiPages || []).length" class="td-menu-empty">No wiki pages yet</li>
                <li v-else-if="!attachChoices.length" class="td-menu-empty">No page matches “{{ attachQuery }}”</li>
              </ul>
            </div>
          </span>
        </h3>
        <ul v-if="attachments.length" class="td-atts">
          <li v-for="w in attachments" :key="w.WikiPageID" class="td-att" :data-test="`task-drawer-attachment-${w.WikiPageID}`">
            <span class="td-att-icon"><td-icon name="book" :size="16" /></span>
            <span class="td-att-body">
              <router-link :to="{ name: 'WikiView', params: { wikiPageID: `${w.WikiPageID}` } }" target="_blank" class="td-att-name">{{ w.Name }}</router-link>
              <span class="td-att-kind">Wiki page</span>
            </span>
            <button
              v-if="canEdit"
              type="button"
              class="td-icon-btn td-icon-btn--sm"
              :aria-label="`Remove ${w.Name}`"
              :data-test="`task-drawer-attachment-remove-${w.WikiPageID}`"
              @click="toggleAttachment(w.WikiPageID)"
            ><td-icon name="x" :size="14" /></button>
          </li>
        </ul>
        <p v-else class="td-hint">Link a wiki page so the how-to is one click away.</p>
      </section>

      <!-- Comments -->
      <section class="td-section" data-test="task-drawer-comments">
        <h3 class="td-h">
          Comments
          <span v-if="comments.length" class="td-count">{{ comments.length }}</span>
        </h3>
        <ul v-if="comments.length" class="td-coms">
          <li v-for="(c, i) in comments" :key="c.id || i" class="td-com" :data-test="`task-drawer-comment-${i}`">
            <span class="td-avatar">{{ initials(c.author) }}</span>
            <div class="td-com-body">
              <div class="td-com-meta">
                <strong>{{ c.author }}</strong>
                <span class="td-com-when">{{ c.when }}</span>
                <span v-if="c.commlog" class="td-com-log">Saved to CommLog</span>
              </div>
              <p class="td-com-text">{{ c.text }}</p>
            </div>
          </li>
        </ul>
        <div class="td-composer">
          <textarea
            v-model="commentText"
            class="td-input td-textarea"
            rows="2"
            placeholder="Write a comment"
            data-test="task-drawer-comment-input"
          ></textarea>
          <div class="td-composer-row">
            <label v-if="patientChips.length" class="td-commlog" data-test="task-drawer-commlog">
              <input
                type="checkbox"
                class="td-checkbox"
                :checked="saveToCommLog"
                data-test="task-drawer-commlog-checkbox"
                @change="onCommLogChange($event)"
              />
              <span>{{ commLogLabel }}</span>
            </label>
            <span class="td-head-spacer"></span>
            <button
              type="button"
              class="td-btn td-btn--primary"
              :disabled="!commentText.trim() || posting"
              data-test="task-drawer-comment-submit"
              @click="postComment"
            >{{ posting ? 'Posting…' : 'Comment' }}</button>
          </div>
          <p v-if="commentError" class="td-error" role="alert" data-test="task-drawer-comment-error">{{ commentError }}</p>
        </div>
      </section>
    </div>

    <footer class="td-foot" data-test="task-drawer-footer">
      <span>Changes save as you go.</span>
      <span
        class="td-save"
        :class="`td-save--${saveState}`"
        role="status"
        aria-live="polite"
        data-test="task-drawer-save-state"
      >{{ saveText }}</span>
    </footer>
  </aside>
</template>

<script>
/**
 * TaskDrawer — the task panel of the redesigned Tasks page (design "Tasks 2a" / "1a" drawer;
 * Phase B2, 2026-10-06). Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md.
 *
 * Opened like TodoDetails (same props, so a host swaps the component and the width):
 *   this.$showPanel({
 *     component: TaskDrawer, openOn: 'right', width: 540,
 *     props: { task, wikiPages, patients, siteTags, onTaskUpdated: t => …, onTaskDeleted: ({ id }) => … },
 *   });
 * ⚠ Pass the callbacks as PROPS: vue2-slideout-panel binds only `closePanel` on the component it
 * renders, so `$showPanel({ listeners })` is silently ignored (found by tasks-my-day.cy.js).
 * After every save it calls onTaskUpdated and emits `task-updated` (the whole task, comments left
 * out); after a delete, onTaskDeleted / `task-deleted` ({ id }); `closePanel` ({}) closes it.
 *
 * EVERY FIELD SAVES AS IT CHANGES (the title 600ms after the last keystroke, or on blur). The
 * payloads come from src/utils/tasks/drawerModel.js, which reproduces TodoDetails.vue's save field
 * by field (cypress/node-tests/tasks-drawer-model.test.js) — read its header for the four
 * deliberate differences. Store actions: tasks/getTaskDetails (on open: full doc + comments),
 * tasks/updateTask (title, assignees, due date, repeat, tags, attachments, reopen, mark-read),
 * tasks/updateTaskSubtasks (subtasks, as TodoDetails), tasks/addTaskAssignmentNotifications,
 * tasks/addSiteTaskTag ("Create tag"), tasks/addTaskComment (+ the commlog flag), tasks/deleteTask /
 * tasks/deleteThisInstanceOfRepeatedTask, and — for "Mark complete" — the Tasks list checkbox's
 * own dispatches via src/utils/tasks/completeTask.js (tasks/addPersonalTask for a repeating task's
 * next instance, then tasks/updateTask).
 *
 * Reads the user, employees, site and settings from the store (state.customer) without the mixin
 * (mixin-prop-shadow-guardrail.test.js). Never reads $route. Popovers are absolutely positioned
 * inside their field and never wider than the drawer.
 */
import moment from 'moment-timezone';
import draggable from 'vuedraggable';
import At from '../vueat/At';
import { readSettingValue } from '@/utils/siteCapability';
import { ensurePatientTagged } from '@/utils/taskPatients';
import { splitTitle, joinTitle } from '@/utils/tasks/titleBadges.js';
import {
  cleanTaskContent, combineStoreTasks, needsNextInstance, findNextInstance, nextInstancePayload, completedTaskPayload,
} from '@/utils/tasks/completeTask.js';
import {
  DEFAULT_TIME_ZONE, withoutComments, contentPatch, canEditAssignees, assigneesPatch, assignmentNotifications,
  dueDayOf, dueDatePatch, dueDatePresets, skipExcludedDays, repeatOptions, repeatKeyOf, repeatSummary, repeatPatch,
  repeats, tagsPatch, tagChoices, cleanTag, subtaskProgress, subtasksPatch, attachedWikiPages, wikiPageIDs,
  wikiPagesPatch, reopenPatch, markReadPatch, commLogTick, commentPayload, deleteRights, deleteTaskPayload,
  deleteThisOnePayload, taskIdLabel, taskLink, taggedPatientIDs,
} from '@/utils/tasks/drawerModel.js';

const TITLE_DEBOUNCE_MS = 600;
const NARROW_PX = 460;

// Lucide path data (ISC/MIT, as FuIcon.vue) on a 24×24 viewBox, stroked in currentColor.
const ICONS = {
  check: ['M20 6 9 17l-5-5'],
  undo: ['M3 7v6h6', 'M21 17a9 9 0 0 0-15-6.7L3 13'],
  link: ['M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7', 'M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7'],
  trash: ['M3 6h18', 'M8 6V4h8v2', 'M19 6l-1 14H6L5 6', 'M10 11v6M14 11v6'],
  x: ['M18 6 6 18M6 6l12 12'],
  plus: ['M12 5v14M5 12h14'],
  cal: ['M8 2.5v4M16 2.5v4M3 9.5h18', 'M5.5 4.5h13A2.5 2.5 0 0 1 21 7v12a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 19V7a2.5 2.5 0 0 1 2.5-2.5z'],
  repeat: ['m17 2 4 4-4 4', 'M3 11v-1a4 4 0 0 1 4-4h14', 'm7 22-4-4 4-4', 'M21 13v1a4 4 0 0 1-4 4H3'],
  tag: ['M12.6 2.6A2 2 0 0 0 11.2 2H4a2 2 0 0 0-2 2v7.2a2 2 0 0 0 .6 1.4l8.7 8.7a2.4 2.4 0 0 0 3.4 0l6.6-6.6a2.4 2.4 0 0 0 0-3.4z', 'M7.5 7.5h.01'],
  book: ['M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20'],
  grip: ['M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01'],
};

const TdIcon = {
  name: 'TdIcon',
  functional: true,
  props: { name: { type: String, required: true }, size: { type: Number, default: 16 } },
  render(h, { props, data }) {
    return h('svg', {
      staticClass: ['td-svg', data.staticClass].filter(Boolean).join(' '),
      class: data.class,
      attrs: {
        width: props.size, height: props.size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
        'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false',
      },
    }, (ICONS[props.name] || []).map(d => h('path', { attrs: { d } })));
  },
};

function nameInitials(name) {
  const clean = String(name || '').replace(/\(.*?\)/g, ' ').trim();
  if (clean.indexOf(',') !== -1) {
    const [last, first] = clean.split(',');
    return `${(first || '').trim().charAt(0)}${(last || '').trim().charAt(0)}`.toUpperCase() || '?';
  }
  const words = clean.split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return `${words[0].charAt(0)}${words.length > 1 ? words[words.length - 1].charAt(0) : ''}`.toUpperCase();
}

/** A store date value (Date, Timestamp, moment, 'MM/DD/YYYY h:mm:ss a[ z]') as a moment, or null. */
function toMoment(value) {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  if (typeof value.toDate === 'function') return moment(value.toDate());
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : moment(value);
  const m = moment(String(value), 'MM/DD/YYYY h:mm:ss a');
  return m.isValid() ? m : null;
}

function newSubtaskId() {
  return `st_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`; // TodoDetails generateSubtaskId
}

export default {
  name: 'TaskDrawer',
  components: { At, draggable, TdIcon },
  props: {
    task: { type: Object, required: true },
    wikiPages: { type: Array, default: () => [] },
    siteTags: { type: Array, default: () => [] },
    patients: { type: Array, default: () => [] },
    // vue2-slideout-panel binds ONLY `closePanel` on the component it renders — `$showPanel({
    // listeners })` is silently ignored (dist/vue-slideout-panel.js render: `on: { closePanel }`).
    // So a host that wants to hear about saves passes these callbacks as props (they are passed
    // through with the other props). The events below are still emitted for a direct parent.
    onTaskUpdated: { type: Function, default: null },
    onTaskDeleted: { type: Function, default: null },
  },

  data() {
    // The title editor shows the text only; the @ / # pills are kept in titleBadges and written back
    // on every save (src/utils/tasks/titleBadges.js — Assigned to / Patient already show them).
    const title = splitTitle(this.task && typeof this.task.content === 'string' ? this.task.content : '',
      typeof document !== 'undefined' ? document : null);
    return {
      doc: Object.assign({}, this.task),
      loadError: '',
      contentValue: title.text,
      titleBadges: title.badges,
      titleBaseline: null,
      titleTimer: null,
      pop: null,
      assignQuery: '',
      patientQuery: '',
      tagQuery: '',
      attachQuery: '',
      subtaskDraft: '',
      commentText: '',
      saveToCommLog: false,
      commentError: '',
      posting: false,
      confirmingDelete: false,
      busy: false,
      copied: false,
      saveState: 'idle',
      saveError: '',
      skipNote: '',
      narrow: false,
      resizeObserver: null,
    };
  },

  computed: {
    customer() {
      return (this.$store && this.$store.state.customer) || {};
    },
    currentUserId() {
      return this.customer.userID;
    },
    currentUser() {
      return this.customer.user || {};
    },
    officeSiteID() {
      return this.customer.siteID;
    },
    employeeList() {
      return Array.isArray(this.customer.employees) ? this.customer.employees : [];
    },
    activeEmployees() {
      return this.employeeList.filter(e => e && (e.Active === 1 || e.Active === '1'));
    },
    patientList() {
      if (Array.isArray(this.patients) && this.patients.length) return this.patients;
      return Array.isArray(this.customer.patients) ? this.customer.patients : [];
    },
    timeZone() {
      return readSettingValue(this.customer.settings, 'GENERAL_TIMEZONE') || DEFAULT_TIME_ZONE;
    },
    today() {
      return moment().format('YYYY-MM-DD');
    },
    canEdit() {
      return !!(this.doc && this.doc.id) && !this.loadError;
    },
    canEditPeople() {
      return this.canEdit && canEditAssignees(this.doc, this.currentUserId);
    },
    isDone() {
      return this.doc.status === 'COMPLETED';
    },
    idLabel() {
      return taskIdLabel(this.doc.id);
    },
    rights() {
      return deleteRights(this.doc, {
        userID: this.currentUserId,
        role: this.currentUser.Role,
        whoCanDelete: readSettingValue(this.customer.settings, 'TASKS_WHO_CAN_DELETE'),
      });
    },
    createdText() {
      const by = this.employeeName(this.doc.createdByEmpID);
      const on = toMoment(this.doc.createdAt);
      if (!by && !on) return '';
      return `Created${by ? ` by ${by}` : ''}${on ? ` on ${this.shortDate(on)}` : ''}`;
    },
    completedText() {
      if (!this.isDone) return '';
      const by = this.employeeName(this.doc.completedByEmpID);
      const on = toMoment(this.doc.completedAt);
      return `Completed${on ? ` ${this.shortDate(on)}` : ''}${by ? ` by ${by}` : ''}`;
    },
    cleaned() {
      return cleanTaskContent(this.doc.content || '', typeof document !== 'undefined' ? document : null);
    },
    patientChips() {
      const seen = {};
      return this.cleaned.patients.filter((p) => {
        if (seen[p.id]) return false;
        seen[p.id] = true;
        return true;
      }).map(p => ({ id: p.id, name: p.name, initials: nameInitials(p.name) }));
    },
    assigneeChips() {
      const ids = Array.isArray(this.doc.assignees) && this.doc.assignees.length
        ? this.doc.assignees
        : [this.doc.assignedToEmpID].filter(id => id !== undefined && id !== null && id !== '');
      return ids.map((id) => {
        const name = this.employeeName(id) || 'Unknown';
        return { id, label: id === this.currentUserId ? 'You' : name, initials: nameInitials(name) };
      });
    },
    assignChoices() {
      const q = this.assignQuery.trim().toLowerCase();
      return this.activeEmployees.filter(e => !q || String(e.Name || '').toLowerCase().indexOf(q) !== -1);
    },
    patientChoices() {
      const q = this.patientQuery.trim().toLowerCase();
      if (q.length < 2) return [];
      const out = [];
      for (let i = 0; i < this.patientList.length && out.length < 8; i += 1) {
        const p = this.patientList[i];
        const label = String((p && (p.PatientWithDOB || p.Patient)) || '').toLowerCase();
        if (label.indexOf(q) !== -1) out.push(p);
      }
      return out;
    },
    dueDay() {
      return dueDayOf(this.doc, this.timeZone);
    },
    dueText() {
      if (!this.dueDay) return 'No due date';
      const m = moment(this.dueDay, 'YYYY-MM-DD');
      return m.format(m.year() === moment().year() ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
    },
    isLate() {
      return !this.isDone && !!this.dueDay && this.dueDay < this.today;
    },
    presets() {
      return dueDatePresets(this.today);
    },
    isRepeating() {
      return repeats(this.doc);
    },
    repeatAnchor() {
      return this.dueDay || this.today;
    },
    repeatChoices() {
      return repeatOptions(this.repeatAnchor);
    },
    repeatKey() {
      return repeatKeyOf(this.doc, this.repeatAnchor);
    },
    repeatText() {
      return repeatSummary(this.doc);
    },
    tagOptions() {
      return tagChoices(this.siteTags, this.doc.tags, this.tagQuery, 8);
    },
    subtasks() {
      return Array.isArray(this.doc.subtasks) ? this.doc.subtasks : [];
    },
    subProgress() {
      return subtaskProgress(this.subtasks);
    },
    attachments() {
      const ids = wikiPageIDs(this.doc).map(String);
      const known = attachedWikiPages(this.doc, this.wikiPages);
      // A page the wikiPages list does not know (deleted, or no list passed) still shows by its ID.
      return ids.map(id => known.find(w => String(w.WikiPageID) === id) || { WikiPageID: id, Name: `Wiki page ${id}` });
    },
    attachChoices() {
      const q = this.attachQuery.trim().toLowerCase();
      return (this.wikiPages || []).filter(w => w && (!q || String(w.Name || '').toLowerCase().indexOf(q) !== -1)).slice(0, 50);
    },
    comments() {
      const list = Array.isArray(this.doc.comments) ? this.doc.comments.slice() : [];
      return list
        .map(c => ({ c, m: toMoment(c && c.createdAt) }))
        .sort((a, b) => (a.m ? a.m.valueOf() : 0) - (b.m ? b.m.valueOf() : 0))
        .map(({ c, m }) => ({
          id: c.id,
          author: c.employeeID === this.currentUserId ? 'You' : (this.employeeName(c.employeeID) || c.createdByEmpName || 'Someone'),
          when: m ? m.format('MMM D, h:mm A') : '',
          text: c.text,
          commlog: c.saveToCommLog === 'TRUE',
        }));
    },
    commLogLabel() {
      if (this.patientChips.length !== 1) return 'Save to CommLog';
      const name = this.patientChips[0].name;
      const parts = name.split(',');
      const display = parts.length > 1 ? `${parts.slice(1).join(',').trim()} ${parts[0].trim()}` : name;
      return `Save to ${display}’s CommLog`;
    },
    saveText() {
      if (this.saveState === 'saving') return 'Saving…';
      if (this.saveState === 'saved') return 'Saved';
      if (this.saveState === 'error') return this.saveError || 'Couldn’t save — try again';
      return '';
    },
    storeTasks() {
      const g = (this.$store && this.$store.getters) || {};
      return combineStoreTasks(g['tasks/myCreatedTasks'], g['tasks/assignedToMeTasks'], g['tasks/myEmployeesTasks']);
    },
  },

  watch: {
    task(next, prev) {
      if (!next || (prev && next.id === prev.id)) return;
      this.doc = Object.assign({}, next);
      this.setTitle(typeof next.content === 'string' ? next.content : '');
      this.load();
    },
  },

  mounted() {
    this.markTitleBaseline();
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.$el);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measure);
    }
    this.load();
  },

  beforeDestroy() {
    if (this.titleTimer) {
      clearTimeout(this.titleTimer);
      this.flushTitle();
    }
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else if (typeof window !== 'undefined') window.removeEventListener('resize', this.measure);
  },

  methods: {
    // ---- helpers -----------------------------------------------------------------------------
    employeeName(id) {
      if (id === undefined || id === null || id === '') return '';
      const e = this.employeeList.find(x => String(x.EmployeeID) === String(id));
      return e ? e.Name : '';
    },
    initials(name) {
      return nameInitials(name);
    },
    shortDate(m) {
      return m.format(m.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY');
    },
    ctx() {
      return { today: this.today, timeZone: this.timeZone, now: new Date() };
    },
    measure() {
      const w = this.$el && this.$el.clientWidth;
      this.narrow = !!w && w < NARROW_PX;
    },
    openPop(name) {
      this.pop = this.pop === name ? null : name;
      this.skipNote = '';
      this.$nextTick(() => {
        const ref = { assign: 'assignSearch', patient: 'patientSearch', tag: 'tagSearch', attach: 'attachSearch' }[this.pop];
        if (ref && this.$refs[ref] && this.$refs[ref].focus) this.$refs[ref].focus();
      });
    },
    closePop() {
      this.pop = null;
      this.assignQuery = '';
      this.patientQuery = '';
      this.tagQuery = '';
      this.attachQuery = '';
    },
    onEscape() {
      if (this.pop) this.closePop();
      else if (this.confirmingDelete) this.confirmingDelete = false;
    },
    emitUpdated(task) {
      const out = withoutComments(task || this.doc);
      this.$emit('task-updated', out);
      if (typeof this.onTaskUpdated === 'function') this.onTaskUpdated(out);
    },
    emitDeleted(id) {
      const out = { id };
      this.$emit('task-deleted', out);
      if (typeof this.onTaskDeleted === 'function') this.onTaskDeleted(out);
    },
    toast(kind, text) {
      if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](text);
    },

    // ---- load --------------------------------------------------------------------------------
    async load() {
      const id = this.doc && this.doc.id;
      if (!id) {
        this.loadError = 'This task could not be opened. Close this and refresh the tasks page.';
        return;
      }
      let full = null;
      try {
        full = await this.$store.dispatch('tasks/getTaskDetails', { taskID: id });
      } catch (err) {
        full = null;
      }
      if (!this.doc || this.doc.id !== id) return; // another task was opened meanwhile
      if (!full) {
        this.loadError = 'This task could not be loaded. It may have been deleted — close this and refresh the tasks page.';
        return;
      }
      this.loadError = '';
      const typing = this.titleTimer !== null;
      this.doc = Object.assign({}, full);
      if (!typing) this.setTitle(typeof full.content === 'string' ? full.content : '');
      const read = markReadPatch(full, this.currentUserId);
      if (read) {
        try {
          await this.$store.dispatch('tasks/updateTask', read);
          this.$set(this.doc, 'unreadCommentsList', read.unreadCommentsList);
        } catch (err) {
          console.error('[TaskDrawer] could not mark the comments read:', err);
        }
      }
    },

    // ---- saving ------------------------------------------------------------------------------
    /** tasks/updateTask with `patch`: applied at once, rolled back if the save fails. */
    async save(patch, action = 'tasks/updateTask', payload) {
      if (!patch) return false;
      const keys = Object.keys(patch).filter(k => k !== 'id');
      const before = {};
      keys.forEach((k) => { before[k] = Object.prototype.hasOwnProperty.call(this.doc, k) ? this.doc[k] : undefined; });
      keys.forEach((k) => this.$set(this.doc, k, patch[k]));
      this.saveState = 'saving';
      this.saveError = '';
      try {
        await this.$store.dispatch(action, payload || patch);
        this.saveState = 'saved';
        this.emitUpdated();
        return true;
      } catch (err) {
        console.error('[TaskDrawer] save failed:', err);
        keys.forEach((k) => {
          if (before[k] === undefined) this.$delete(this.doc, k);
          else this.$set(this.doc, k, before[k]);
        });
        this.saveState = 'error';
        this.saveError = 'Couldn’t save that change — try again.';
        return false;
      }
    },

    // ---- title -------------------------------------------------------------------------------
    // What the title editor holds — the text WITHOUT the kept-aside pills.
    editorHtml() {
      const el = this.$refs.editor;
      return el ? el.innerHTML : (this.contentValue || '');
    },
    // The full content to save: the editor's text + the pills kept aside.
    titleHtml() {
      return joinTitle(this.editorHtml(), this.titleBadges);
    },
    markTitleBaseline() {
      this.$nextTick(() => {
        this.$nextTick(() => { this.titleBaseline = this.editorHtml(); });
      });
    },
    setTitle(html) {
      const title = splitTitle(html, typeof document !== 'undefined' ? document : null);
      this.titleBadges = title.badges;
      this.contentValue = title.text;
      this.markTitleBaseline();
    },
    onTitleInput(html) {
      if (this.titleBaseline === null || html === this.titleBaseline) return;
      if (this.titleTimer) clearTimeout(this.titleTimer);
      this.titleTimer = setTimeout(() => this.flushTitle(), TITLE_DEBOUNCE_MS);
    },
    async flushTitle() {
      if (this.titleTimer) clearTimeout(this.titleTimer);
      this.titleTimer = null;
      if (!this.canEdit) return;
      const display = this.editorHtml();
      if (display === this.titleBaseline) return;
      const html = joinTitle(display, this.titleBadges);
      if (html === this.doc.content) return;
      // The kept-aside pills never make an emptied title "non-empty": judge what the user sees.
      const patch = contentPatch(this.doc, display, this.ctx()) && contentPatch(this.doc, html, this.ctx());
      if (!patch) {
        this.saveState = 'error';
        this.saveError = 'A task needs a title — type something to save it.';
        return;
      }
      this.titleBaseline = display;
      this.contentValue = display;
      await this.save(patch);
    },
    async saveContent(html) {
      const patch = contentPatch(this.doc, html, this.ctx());
      if (!patch) return;
      this.setTitle(html);
      await this.save(patch);
    },

    // ---- patients (badges in the title, as TodoDetails' # picker writes them) ----------------
    async addPatient(p) {
      if (this.titleTimer) await this.flushTitle();
      let html;
      try {
        html = ensurePatientTagged(this.titleHtml(), {
          siteID: this.officeSiteID, PatNum: p.PatNum, LName: p.LName, FName: p.FName,
        });
      } catch (err) {
        html = null;
      }
      this.closePop();
      if (html && html !== this.doc.content) await this.saveContent(html);
    },
    async removePatient(patNum) {
      if (this.titleTimer) await this.flushTitle();
      if (typeof document === 'undefined') return;
      const box = document.createElement('div');
      box.innerHTML = this.titleHtml();
      box.querySelectorAll(`[data-teamio-patientid="${String(patNum).replace(/"/g, '')}"]`).forEach((badge) => {
        let node = badge;
        const wrap = badge.parentElement;
        if (wrap && wrap !== box && wrap.tagName === 'SPAN' && wrap.children.length === 1) node = wrap;
        node.parentNode.removeChild(node);
      });
      const html = box.innerHTML;
      if (taggedPatientIDs(html).length === taggedPatientIDs(this.doc.content).length) return;
      await this.saveContent(html);
    },

    // ---- assignees ---------------------------------------------------------------------------
    isAssigned(id) {
      return (this.doc.assignees || []).some(x => String(x) === String(id));
    },
    async toggleAssignee(id) {
      const before = Array.isArray(this.doc.assignees) ? this.doc.assignees.slice() : this.doc.assignees;
      const list = Array.isArray(before) ? before.slice() : [];
      const next = this.isAssigned(id) ? list.filter(x => String(x) !== String(id)) : list.concat([id]);
      const ok = await this.save(assigneesPatch(this.doc, next, this.ctx()));
      if (!ok) return;
      const notifications = assignmentNotifications(before, next, {
        userID: this.currentUserId,
        userName: this.currentUser.Name,
        siteID: this.officeSiteID,
        taskID: this.doc.id,
        now: new Date(),
      });
      if (notifications.length) {
        try {
          await this.$store.dispatch('tasks/addTaskAssignmentNotifications', { notifications, taskID: this.doc.id });
        } catch (err) {
          console.error('[TaskDrawer] assignment notifications failed:', err);
        }
      }
    },

    // ---- due date / repeat -------------------------------------------------------------------
    async setDue(ymd) {
      if (!ymd) return;
      const patch = dueDatePatch(this.doc, ymd, this.ctx());
      if (!patch) return;
      const landed = skipExcludedDays(this.doc, ymd);
      const ok = await this.save(patch);
      if (ok && landed !== ymd) {
        this.skipNote = `Moved to ${moment(landed, 'YYYY-MM-DD').format('ddd, MMM D')} — this task skips weekends.`;
        return;
      }
      if (ok) this.closePop();
    },
    async setRepeat(key) {
      const patch = repeatPatch(this.doc, key, Object.assign(this.ctx(), { anchor: this.repeatAnchor }));
      this.closePop();
      await this.save(patch);
    },

    // ---- tags --------------------------------------------------------------------------------
    async addTag(tag) {
      const v = cleanTag(tag);
      if (!v) return;
      this.tagQuery = '';
      await this.save(tagsPatch(this.doc, (this.doc.tags || []).concat([v]), this.ctx()));
    },
    async removeTag(tag) {
      await this.save(tagsPatch(this.doc, (this.doc.tags || []).filter(g => g !== tag), this.ctx()));
    },
    async createTag(tag) {
      const v = cleanTag(tag);
      if (!v) return;
      try {
        await this.$store.dispatch('tasks/addSiteTaskTag', { siteID: this.officeSiteID, tagName: v });
      } catch (err) {
        // tasks/updateTask syncs the task's tags into the site list again on save.
        console.error('[TaskDrawer] could not add the site tag:', err);
      }
      await this.addTag(v);
    },
    onTagEnter() {
      const o = this.tagOptions;
      if (o.create) this.createTag(o.create);
      else if (o.suggestions.length) this.addTag(o.suggestions[0]);
    },

    // ---- subtasks (tasks/updateTaskSubtasks, as TodoDetails autosaves them) -----------------
    async saveSubtasks(op) {
      const patch = subtasksPatch(this.doc, op, Object.assign(this.ctx(), { userID: this.currentUserId }));
      if (!patch) return false;
      return this.save({ id: patch.id, subtasks: patch.subtasks }, 'tasks/updateTaskSubtasks', { taskId: patch.id, subtasks: patch.subtasks });
    },
    async addSubtask() {
      const content = this.subtaskDraft.trim();
      if (!content) return;
      this.subtaskDraft = '';
      const ok = await this.saveSubtasks({ type: 'add', content, id: newSubtaskId() });
      if (!ok) this.subtaskDraft = content;
    },
    toggleSubtask(index) {
      return this.saveSubtasks({ type: 'toggle', index });
    },
    removeSubtask(index) {
      return this.saveSubtasks({ type: 'remove', index });
    },
    onSubtaskDragEnd(evt) {
      if (!evt || evt.oldIndex === evt.newIndex) return null;
      return this.saveSubtasks({ type: 'move', from: evt.oldIndex, to: evt.newIndex });
    },

    // ---- attachments -------------------------------------------------------------------------
    isAttached(id) {
      return wikiPageIDs(this.doc).some(x => String(x) === String(id));
    },
    async toggleAttachment(id) {
      const ids = wikiPageIDs(this.doc);
      const next = this.isAttached(id) ? ids.filter(x => String(x) !== String(id)) : ids.concat([id]);
      await this.save(wikiPagesPatch(this.doc, next, this.ctx()));
    },

    // ---- comments ----------------------------------------------------------------------------
    onCommLogChange(event) {
      const tick = commLogTick(!!event.target.checked, taggedPatientIDs(this.doc.content).length);
      this.saveToCommLog = tick.checked;
      event.target.checked = tick.checked;
      if (tick.warning) {
        this.commentError = tick.warning;
        this.toast('warning', tick.warning);
      } else {
        this.commentError = '';
      }
    },
    async postComment() {
      if (this.posting) return;
      if (this.titleTimer) await this.flushTitle();
      const tagged = taggedPatientIDs(this.doc.content).length;
      if (this.saveToCommLog && tagged !== 1) {
        this.commentError = tagged === 0
          ? 'You are trying to save this comment to comm log but you do not have any patient tagged in this task. Please tag a patient or uncheck the option to save to comm log'
          : 'You are trying to save the comment to comm log but you have multiple patients tagged. Please remove one before saving or uncheck the save to comm log checkbox';
        return;
      }
      const payload = commentPayload(this.doc, {
        text: this.commentText,
        saveToCommLog: this.saveToCommLog,
        userID: this.currentUserId,
        user: this.currentUser,
        now: new Date(),
      });
      if (!payload) return;
      this.posting = true;
      this.commentError = '';
      const countBefore = this.comments.length;
      try {
        await this.$store.dispatch('tasks/addTaskComment', payload);
        const fresh = await this.$store.dispatch('tasks/getTaskDetails', { taskID: this.doc.id });
        const freshComments = fresh && Array.isArray(fresh.comments) ? fresh.comments : null;
        if (freshComments) this.$set(this.doc, 'comments', freshComments);
        // tasks/addTaskComment reports its own failures (alert) instead of throwing.
        if (freshComments && freshComments.length <= countBefore) {
          this.commentError = 'The comment could not be saved. Please try again.';
          return;
        }
        this.commentText = '';
        this.saveToCommLog = false;
        this.emitUpdated();
      } catch (err) {
        console.error('[TaskDrawer] comment failed:', err);
        this.commentError = 'The comment could not be saved. Please try again.';
      } finally {
        this.posting = false;
      }
    },

    // ---- complete / reopen -------------------------------------------------------------------
    textOf(content) {
      return cleanTaskContent(content, typeof document !== 'undefined' ? document : null).text;
    },
    async toggleComplete() {
      if (this.busy || !this.canEdit) return;
      if (this.titleTimer) await this.flushTitle();
      this.busy = true;
      try {
        if (this.isDone) {
          await this.save(reopenPatch(this.doc, this.ctx()));
          return;
        }
        // The Tasks list checkbox (TodoItem.changeStatus + handleItemChange), via completeTask.js.
        const item = withoutComments(this.doc);
        const diff = moment(item.dueAtStr).diff(moment(), 'days');
        if (needsNextInstance(item, diff)) {
          const nextDueAt = this.$store.getters['tasks/getNextDueDate'](item).toDate();
          const nextDueAtStr = moment(nextDueAt).format('MM/DD/YYYY');
          if (!findNextInstance(item, this.storeTasks, nextDueAtStr, this.textOf)) {
            await this.$store.dispatch('tasks/addPersonalTask', nextInstancePayload(item, {
              createdAt: moment().toDate(),
              nextDueAt,
              nextDueAtStr,
            }));
          }
        }
        const change = completedTaskPayload(item, {
          dueAt: moment(item.dueAt).toDate(),
          dueAtStr: moment(item.dueAt).format('MM/DD/YYYY'),
          createdAt: moment(item.createdAt).toDate(),
          completedAt: moment().toDate(),
          completedAtStr: moment().format('MM/DD/YYYY'),
          completedByEmpID: this.currentUserId,
        });
        this.saveState = 'saving';
        await this.$store.dispatch('tasks/updateTask', change);
        Object.keys(change).forEach(k => this.$set(this.doc, k, change[k]));
        this.saveState = 'saved';
        this.emitUpdated();
      } catch (err) {
        console.error('[TaskDrawer] completing the task failed:', err);
        this.saveState = 'error';
        this.saveError = 'The task could not be completed. Please try again in a moment.';
      } finally {
        this.busy = false;
      }
    },

    // ---- delete ------------------------------------------------------------------------------
    async deleteAll() {
      if (!this.rights.all || this.busy) return;
      this.busy = true;
      try {
        await this.$store.dispatch('tasks/deleteTask', deleteTaskPayload(this.doc, { userID: this.currentUserId, now: new Date() }));
        this.emitDeleted(this.doc.id);
        this.$emit('closePanel', {});
      } catch (err) {
        console.error('[TaskDrawer] delete failed:', err);
        this.saveState = 'error';
        this.saveError = 'An error occurred while trying to delete the task.';
      } finally {
        this.busy = false;
      }
    },
    async deleteThisOne() {
      if (!this.rights.thisOne || this.busy) return;
      this.busy = true;
      try {
        const payload = deleteThisOnePayload(this.doc, { now: new Date(), timeZone: this.timeZone });
        await this.$store.dispatch('tasks/deleteThisInstanceOfRepeatedTask', payload);
        this.emitUpdated(payload.task);
        this.$emit('closePanel', {});
      } catch (err) {
        console.error('[TaskDrawer] deleting this instance failed:', err);
        this.saveState = 'error';
        this.saveError = 'An error occurred while trying to delete this task instance.';
      } finally {
        this.busy = false;
      }
    },

    // ---- header ------------------------------------------------------------------------------
    async copyLink() {
      const url = taskLink(typeof window !== 'undefined' ? window.location.origin : '', this.doc.id);
      try {
        await navigator.clipboard.writeText(url);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        this.toast('error', 'Failed to copy the link.');
      }
    },
    async close() {
      if (this.titleTimer) await this.flushTitle();
      this.$emit('closePanel', {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.td {
  @include fu-font-base;
  position: relative;
  display: flex;
  flex-direction: column;
  // Exactly the slide-out's height (vue2-slideout-panel `.slideout` is fixed, top 0 / bottom 0), with
  // the BODY as the scroller: the panel itself never overflows, so it draws no scrollbar and the
  // drawer keeps its full 540px (a scrolling panel left a 15px gutter — tasks-my-day.cy.js geometry).
  height: 100%;
  min-height: 0;
  background: $fu-surface;
  font-size: $fu-fs-body-sm;
}

.td-scrim {
  position: fixed;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 5;
  background: transparent;
}

// ---- Header ----------------------------------------------------------------------------------
.td-head {
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: $fu-space-1;
  padding: $fu-space-3 $fu-space-4;
  background: $fu-surface;
  border-bottom: 1px solid $fu-border;
}
.td-head-spacer { flex: 1 1 auto; }

.td-complete {
  @include fu-btn(secondary, sm);
  gap: 6px;
  &--done { color: $fu-text-muted; }
}

.td-id {
  @include fu-tabular;
  margin-right: $fu-space-1;
  font-size: $fu-fs-caption;
  color: $fu-text-subtle;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.td-icon-btn {
  @include fu-icon-btn(34px);
  &--sm { width: 28px; height: 28px; }
}

.td-confirm {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $fu-space-2;
  margin: $fu-space-3 $fu-space-4 0;
  padding: $fu-space-3;
  border: 1px solid $fu-danger-border;
  border-radius: $fu-radius-md;
  background: $fu-danger-subtle;
}
.td-confirm-text { flex: 1 1 160px; font-weight: $fu-fw-semibold; color: $fu-danger-text; }
.td-confirm-actions { display: flex; flex-wrap: wrap; gap: $fu-space-2; }

.td-btn {
  &--primary { @include fu-btn(primary, sm); }
  &--secondary { @include fu-btn(secondary, sm); }
  &--danger { @include fu-btn(danger, sm); }
  &--ghost { @include fu-btn(ghost, sm); }
}

// ---- Body ------------------------------------------------------------------------------------
.td-body {
  flex: 1 1 auto;
  padding: $fu-space-5 $fu-space-6 $fu-space-4;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.td--narrow .td-body { padding: $fu-space-4; }

.td-notice {
  margin-bottom: $fu-space-4;
  padding: $fu-space-3;
  border-radius: $fu-radius-md;
  background: $fu-warning-subtle;
  color: $fu-warning-text;
}

.td-title-wrap { margin-bottom: $fu-space-5; }

.td-title {
  @include fu-font-display;
  min-height: 32px;
  padding: 2px 4px;
  margin: 0 -4px;
  border-radius: $fu-radius-sm;
  font-size: 22px;
  font-weight: $fu-fw-bold;
  line-height: 1.3;
  color: $fu-text;
  word-break: break-word;
  outline: none;
  &:hover { background: $fu-neutral-50; }
  &:focus { background: $fu-surface; box-shadow: $fu-ring; }
  &:empty::before { content: attr(placeholder); color: $fu-text-subtle; }
  &--done { color: $fu-text-subtle; text-decoration: line-through; }

  ::v-deep .badge {
    font-family: $fu-font-sans;
    font-size: 13px;
    font-weight: $fu-fw-semibold;
    vertical-align: middle;
  }
}

.td-created {
  margin: 6px 0 0;
  font-size: $fu-fs-caption;
  color: $fu-text-muted;
  span + span::before { content: ' · '; }
}
.td-created-done { color: $fu-success; }

// ---- Property grid ---------------------------------------------------------------------------
.td-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $fu-space-5 $fu-space-6;
  margin-bottom: $fu-space-6;
}
.td--narrow .td-grid { grid-template-columns: minmax(0, 1fr); gap: $fu-space-4; }

.td-field {
  position: relative;
  min-width: 0;
  &--wide { grid-column: 1 / -1; }
}

.td-label {
  @include fu-eyebrow;
  display: block;
  margin-bottom: $fu-space-2;
}

.td-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.td-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-md;
  background: $fu-surface;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  &--person { padding-left: 5px; }
}

.td-chip-x {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  margin-left: 2px;
  padding: 2px;
  border-radius: $fu-radius-xs;
  color: $fu-text-subtle;
  &:hover { color: $fu-text; background: $fu-neutral-100; }
}

.td-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  font-size: 10px;
  font-weight: $fu-fw-bold;
  &--patient { background: $fu-brand-50; color: $fu-brand-700; }
}

.td-add {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border: 1px dashed $fu-border-strong;
  border-radius: $fu-radius-md;
  color: $fu-text-muted;
  font-weight: $fu-fw-semibold;
  white-space: nowrap;
  &:hover { color: $fu-text; border-color: $fu-brand-300; background: $fu-neutral-50; }
}

.td-pick {
  @include fu-btn(secondary, sm);
  gap: 7px;
  max-width: 100%;
  &--muted { color: $fu-text-muted; }
  &--late { color: $fu-danger-text; border-color: $fu-danger-border; }
}

.td-empty { color: $fu-text-subtle; }

// ---- Popovers (inside their field; never wider than the drawer) -----------------------------
.td-pop {
  @include fu-popover-surface;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 6;
  width: 300px;
  max-width: 100%;
  max-height: 340px;
  margin-top: 6px;
  padding: $fu-space-2;
  &--end { left: auto; right: 0; }
}
.td-field:not(.td-field--wide) .td-pop { max-width: none; }
.td--narrow .td-field .td-pop { left: 0; right: auto; max-width: 100%; }
.td-attach-wrap .td-pop { max-width: calc(100vw - 32px); }

.td-input {
  @include fu-input(38px);
}
.td-textarea {
  height: auto;
  min-height: 64px;
  padding: 10px 12px;
  resize: vertical;
}

.td-menu {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.td-menu-item {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  width: 100%;
  min-height: 38px;
  padding: 6px 10px;
  border-radius: $fu-radius-sm + 2px;
  color: $fu-text;
  &:hover { background: $fu-neutral-50; }
  &--on { font-weight: $fu-fw-semibold; }
  &--static { cursor: default; &:hover { background: none; } }
  &--create { color: $fu-brand-700; background: $fu-brand-50; &:hover { background: $fu-brand-100; } }
}
.td-menu-text { flex: 1 1 auto; min-width: 0; @include fu-ellipsis; }
.td-menu-check { color: $fu-brand; }
.td-menu-empty { padding: 8px 10px; color: $fu-text-muted; font-size: $fu-fs-caption; }

.td-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: $fu-space-3;
}
.td-preset { @include fu-chip(false); padding: 6px 11px; }
.td-preset--on { @include fu-chip(true); padding: 6px 11px; }

.td-date-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: $fu-fs-caption;
  color: $fu-text-muted;
}

.td-hint {
  margin: $fu-space-2 0 0;
  font-size: $fu-fs-caption;
  color: $fu-text-muted;
}

// ---- Sections --------------------------------------------------------------------------------
.td-section { margin-bottom: $fu-space-6; }

.td-h {
  position: relative;
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  margin: 0 0 $fu-space-3;
  font-family: $fu-font-display;
  font-size: 15px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.td-count {
  @include fu-tabular;
  font-size: $fu-fs-caption;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
}

.td-link-btn { @include fu-link-btn; color: $fu-text; font-family: $fu-font-sans; font-size: $fu-fs-body-sm; }
.td-attach-wrap { position: relative; }

.td-subs { list-style: none; margin: 0 0 $fu-space-2; padding: 0; }

.td-sub {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  min-height: 38px;
  padding: 2px 4px;
  border-radius: $fu-radius-sm;
  &:hover { background: $fu-neutral-50; }
  &--done .td-sub-text { color: $fu-text-subtle; text-decoration: line-through; }
}
.td-sub-grip { display: inline-flex; color: $fu-text-subtle; cursor: grab; }
.td-sub-text { flex: 1 1 auto; min-width: 0; word-break: break-word; }

.td-check {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 20px;
  height: 20px;
  border: 1.5px solid $fu-border-strong;
  border-radius: 50%;
  color: #ffffff;
  &--on { background: $fu-brand; border-color: $fu-brand; }
}

.td-sub-add {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  margin: 0;
  padding: 0 12px;
  border: 1px solid $fu-border-strong;
  border-radius: $fu-radius-md;
  color: $fu-text-subtle;
  &:focus-within { border-color: $fu-brand-400; box-shadow: $fu-ring; }
}
.td-sub-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 42px;
  border: 0;
  outline: none;
  background: transparent;
  font-family: $fu-font-sans;
  font-size: $fu-fs-body-sm;
  color: $fu-text;
  &::placeholder { color: $fu-text-subtle; }
  @include fu-phone { font-size: 16px; }
}

.td-atts { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: $fu-space-2; }
.td-att {
  display: flex;
  align-items: center;
  gap: $fu-space-3;
  padding: $fu-space-3;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-md;
}
.td-att-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: $fu-radius-sm + 2px;
  background: $fu-neutral-50;
  color: $fu-neutral-600;
}
.td-att-body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.td-att-name { @include fu-ellipsis; font-weight: $fu-fw-semibold; color: $fu-link; }
.td-att-kind { font-size: 12px; color: $fu-text-muted; }

.td-coms { list-style: none; margin: 0 0 $fu-space-4; padding: 0; display: flex; flex-direction: column; gap: $fu-space-3; }
.td-com { display: flex; gap: 10px; }
.td-com .td-avatar { width: 28px; height: 28px; }
.td-com-body { flex: 1 1 auto; min-width: 0; }
.td-com-meta { display: flex; flex-wrap: wrap; align-items: baseline; gap: $fu-space-2; font-size: $fu-fs-caption; }
.td-com-when { color: $fu-text-muted; }
.td-com-log { @include fu-pill($fu-brand-50, $fu-brand-700); }
.td-com-text { margin: 2px 0 0; white-space: pre-wrap; word-break: break-word; }

.td-composer {
  padding: $fu-space-3;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-lg;
  background: $fu-surface-sunken;
}
.td-composer-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $fu-space-2;
  margin-top: $fu-space-2;
}
.td-commlog {
  display: inline-flex;
  align-items: center;
  gap: $fu-space-2;
  margin: 0;
  min-width: 0;
  font-size: $fu-fs-caption;
  color: $fu-text-strong;
  cursor: pointer;
}
.td-checkbox { @include fu-checkbox; }
.td-error { margin: $fu-space-2 0 0; font-size: $fu-fs-caption; color: $fu-danger-text; }

// ---- Footer ----------------------------------------------------------------------------------
.td-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $fu-space-3;
  padding: $fu-space-3 $fu-space-6;
  border-top: 1px solid $fu-border;
  font-size: 12px;
  color: $fu-text-muted;
}
.td--narrow .td-foot { padding: $fu-space-3 $fu-space-4; }
.td-save {
  font-weight: $fu-fw-semibold;
  &--saved { color: $fu-success; }
  &--error { color: $fu-danger-text; }
}

.td-svg { flex: none; }
</style>
